import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Plus, FileText, Upload, X, Receipt, Loader,
    Briefcase, Info, Eye, AlertCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import {
    getMyNoteDeFraisRequestsRequest,
    createNoteDeFraisRequestRequest,
} from "../../../../redux/actions/noteDeFraisRequestActions";
import { fetchNoteDeFraisRequest } from "../../../../redux/actions/noteDeFraisActions";
import { fetchOrdreMissionActifsRequest } from "../../../../redux/actions/ordreMissionActions";
import { getMyMissionRequestsRequest } from "../../../../redux/actions/missionRequestActions";
import "../../parametres/parametresStyles/ParamConges.css";

/* ─── helpers de style partagés ─────────────────────────── */
const btn = (extra = {}) => ({
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "7px 14px", borderRadius: 8, fontSize: 12,
    fontWeight: 600, cursor: "pointer", transition: "all .15s",
    ...extra,
});

const CATEGORY_COLOR = {
    TRANSPORT:     { text: "#1d4ed8", bg: "#dbeafe", border: "#93c5fd" },
    HEBERGEMENT:   { text: "#6d28d9", bg: "#ede9fe", border: "#c4b5fd" },
    RESTAURATION:  { text: "#065f46", bg: "#d1fae5", border: "#6ee7b7" },
    AUTRE:         { text: "#92400e", bg: "#fef3c7", border: "#fcd34d" },
};

const STATUS_CONFIG = {
    PENDING_MANAGER:  { label: "En attente du manager", dot: "#f59e0b", color: "#b45309", bg: "#fef3c7", border: "#fcd34d" },
    PENDING_ADMIN:    { label: "Attente de l'admin RH",       dot: "#f59e0b", color: "#b45309", bg: "#fef3c7", border: "#fcd34d" },
    APPROVED:         { label: "Approuvé",          dot: "#16a34a", color: "#166534", bg: "#dcfce7", border: "#86efac" },
    REJECTED:         { label: "Rejeté",            dot: "#ef4444", color: "#991b1b", bg: "#fee2e2", border: "#fca5a5" },
    PAID:             { label: "Remboursé",         dot: "#059669", color: "#065f46", bg: "#d1fae5", border: "#6ee7b7" },
    CANCELLED:        { label: "Annulé",            dot: "#9ca3af", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" },
};

const StatutBadge = ({ statut, demande }) => {
    if (statut === "APPROVED" && demande?.statutRemboursement === "EN_ATTENTE") {
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#075985", background: "#e0f2fe", border: "1px solid #7dd3fc", whiteSpace: "nowrap" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0ea5e9", flexShrink: 0 }} />
                Attente remboursement
            </span>
        );
    }
    const c = STATUS_CONFIG[statut] || { label: statut, dot: "#9ca3af", color: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: c.color, background: c.bg, border: `1px solid ${c.border}`, whiteSpace: "nowrap" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
            {c.label}
        </span>
    );
};

const CategoryChip = ({ cat }) => {
    const c = CATEGORY_COLOR[cat] || { text: "#374151", bg: "#f3f4f6", border: "#d1d5db" };
    return (
        <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 5, fontSize: 11, fontWeight: 700, color: c.text, background: c.bg, border: `1px solid ${c.border}` }}>
            {cat}
        </span>
    );
};

const FieldBlock = ({ label, children }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</label>
        {children}
    </div>
);

const ReadBox = ({ children }) => (
    <div style={{ whiteSpace: "pre-wrap", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
        {children}
    </div>
);

/* ─── composant principal ────────────────────────────────── */
const ExpenseManagement = () => {
    const dispatch = useDispatch();

    const { myRequests, loading } = useSelector((s) => s.noteDeFraisRequests || { myRequests: [], loading: false });
    const { notesDeFrais }        = useSelector((s) => s.noteDeFrais        || { notesDeFrais: [] });
    const { ordresMissionActifs } = useSelector((s) => s.ordreMission       || { ordresMissionActifs: [] });
    const { myRequests: myMissionRequests } = useSelector((s) => s.missionRequests || { myRequests: [] });

    const [showModal,          setShowModal]          = useState(false);
    const [showDetailsModal,   setShowDetailsModal]   = useState(false);
    const [showMissionModal,   setShowMissionModal]   = useState(false);
    const [selectedDemande,    setSelectedDemande]    = useState(null);

    const [form, setForm] = useState({
        ordreMissionId: "", noteDeFraisId: "", category: "",
        dateDepense: "", montant: "", description: "",
        centreCouts: "", urgence: false, justificatifs: [],
    });

    useEffect(() => {
        dispatch(fetchNoteDeFraisRequest());
        dispatch(getMyNoteDeFraisRequestsRequest());
        dispatch(fetchOrdreMissionActifsRequest());
        dispatch(getMyMissionRequestsRequest());
    }, [dispatch]);

    const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
    const formatMontant = (v) => (v == null || isNaN(v)) ? "0" : new Intl.NumberFormat("fr-MA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

    const getRequestCategory = (d) => d?.category || d?.noteDeFrais?.category || d?.requestType || "-";
    const getRequestCode     = (d) => d?.code     || d?.noteDeFrais?.code     || "";

    const myApprovedMissions = useMemo(() => (myMissionRequests || []).filter(r => r.status === "APPROVED"), [myMissionRequests]);

    const selectedMissionRequest = useMemo(() =>
            form.ordreMissionId ? myApprovedMissions.find(r => r.ordreMissionId === Number(form.ordreMissionId)) || null : null,
        [form.ordreMissionId, myApprovedMissions]);

    const selectedOM = useMemo(() =>
            form.ordreMissionId
                ? ordresMissionActifs.find(om => om.id === Number(form.ordreMissionId)) || selectedMissionRequest || null
                : null,
        [form.ordreMissionId, ordresMissionActifs, selectedMissionRequest]);

    const getMissionField = (mission, ordreKey, requestKey) => mission?.[ordreKey] || mission?.[requestKey] || "";
    const getMissionDateDebut = (mission) => getMissionField(mission, "dateDebut", "ordreMissionDateDebut");
    const getMissionDateFin = (mission) => getMissionField(mission, "dateFin", "ordreMissionDateFin");
    const getMissionLieu = (mission) =>
        getMissionField(mission, "lieu", "ordreMissionLieu") ||
        getMissionField(mission, "ville", "ordreMissionVille") ||
        getMissionField(mission, "adresse", "ordreMissionAdresse");

    const typesDisponibles = useMemo(() =>
            selectedOM ? (selectedOM.notesDeFraisAutorisees || []) : notesDeFrais.filter(n => n.actif),
        [selectedOM, notesDeFrais]);

    const maxDate = useMemo(() => {
        const today = new Date().toISOString().split("T")[0];
        const dateFin = getMissionDateFin(selectedOM);
        return dateFin && dateFin < today ? dateFin : today;
    }, [selectedOM]);

    const handleOMChange = (e) => {
        const id = e.target.value;
        const mission = ordresMissionActifs.find(om => om.id === Number(id)) ||
            myApprovedMissions.find(r => r.ordreMissionId === Number(id));
        const dateDebut = getMissionDateDebut(mission);
        const dateFin = getMissionDateFin(mission);
        setForm(p => ({
            ...p, ordreMissionId: id, noteDeFraisId: "", category: "",
            centreCouts: mission?.centreCout || mission?.ordreMissionCentreCout || p.centreCouts,
            dateDepense: mission && dateDebut && (!p.dateDepense || p.dateDepense < dateDebut || p.dateDepense > dateFin)
                ? dateDebut : p.dateDepense,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const type = typesDisponibles.find(t => t.id === Number(form.noteDeFraisId));
        if (!type) return Swal.fire({ icon: "error", title: "Erreur", text: "Veuillez sélectionner un type de frais." });
        const montant = Number(form.montant);
        if (type.montantMinimum && montant < type.montantMinimum)
            return Swal.fire({ icon: "error", title: "Montant trop faible", text: `Minimum : ${formatMontant(type.montantMinimum)} ${type.devise || "MAD"}` });
        if (type.montantMaximum && montant > type.montantMaximum)
            return Swal.fire({ icon: "error", title: "Montant trop élevé", text: `Maximum : ${formatMontant(type.montantMaximum)} ${type.devise || "MAD"}` });
        if (type.obligatoire && form.justificatifs.length === 0)
            return Swal.fire({ icon: "error", title: "Justificatif requis", text: `Un justificatif (${type.typeJustificatif || "requis"}) est obligatoire.` });

        dispatch(createNoteDeFraisRequestRequest({
            noteDeFraisId: Number(form.noteDeFraisId),
            ordreMissionId: form.ordreMissionId ? Number(form.ordreMissionId) : null,
            dateDepense: form.dateDepense, montantDepense: montant,
            description: form.description, centreCouts: form.centreCouts || null,
            urgent: form.urgence, requestType: type.category || "NOTE_FRAIS",
            justificatifs: form.justificatifs, devise: type.devise || "MAD",
        }));
        setShowModal(false);
        setForm({ ordreMissionId: "", noteDeFraisId: "", category: "", dateDepense: "", montant: "", description: "", centreCouts: "", urgence: false, justificatifs: [] });
    };

    const selectedType = typesDisponibles.find(t => t.id === Number(form.noteDeFraisId));

    return (
        <div className="pcPage">

            {/* ── Header ── */}
            <div style={{ margin: "0 18px 10px 18px", padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 20, borderBottom: "1px solid #f1f5f9" }}>
                <div>
                    <h1 style={{ margin: 0, color: "var(--admin-primary)", fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>Mes notes de frais</h1>
                    <p style={{ margin: "5px 0 0", color: "#94a3b8", fontSize: 12 }}>Déclarez vos frais professionnels et consultez vos remboursements.</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    style={{ ...btn({ padding: "9px 16px", border: "none", background: "var(--admin-accent)", color: "#fff", fontSize: 13, boxShadow: "0 1px 3px rgba(0,0,0,.12)", whiteSpace: "nowrap", flexShrink: 0 }) }}
                    onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                    onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                >
                    <Plus size={15} /> Nouvelle note de frais
                </button>
            </div>

            {/* ── Card table ── */}
            <div style={{ padding: "0 18px 28px" }}>
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, boxShadow: "0 1px 3px rgba(16,24,40,.05)", overflow: "hidden" }}>
                    <div style={{ padding: "12px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--admin-primary)" }}>Historique des déclarations</h2>
                        {myRequests.length > 0 && (
                            <span style={{ padding: "2px 9px", borderRadius: 999, background: "#f0f4ff", color: "var(--admin-accent)", fontSize: 11, fontWeight: 700, border: "1px solid #dde6fb" }}>
                                {myRequests.length} déclaration{myRequests.length > 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                    <div style={{ padding: 14 }}>
                        {loading && myRequests.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                                <Loader size={28} className="spin" style={{ color: "var(--admin-accent)", opacity: .7 }} />
                            </div>
                        ) : myRequests.length === 0 ? (
                            <div style={{ padding: "40px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "#94a3b8" }}>
                                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#f8fafc", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <Receipt size={22} style={{ opacity: .5 }} />
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Aucune demande de note de frais.</div>
                                <div style={{ fontSize: 12, color: "#94a3b8" }}>Cliquez sur « Nouvelle note de frais » pour commencer.</div>
                            </div>
                        ) : (
                            <div style={{ width: "100%", overflowX: "auto", borderRadius: 10, border: "1px solid #eef1f5" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 780 }}>
                                    <thead>
                                    <tr>
                                        {["Catégorie & date", "Montant", "Devise", "Mission liée", "Statut", ""].map((h, i) => (
                                            <th key={i} style={{ background: "#f8f9fa", color: "#6b7280", fontSize: 11, fontWeight: 700, textAlign: i === 5 ? "right" : "left", padding: "10px 12px", borderBottom: "1px solid #e5e7eb", textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {myRequests.map((d, idx) => (
                                        <tr key={d.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fcfcfd" }}>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                                                    <CategoryChip cat={getRequestCategory(d)} />
                                                    {d.urgent && <span style={{ padding: "1px 6px", borderRadius: 4, background: "#fee2e2", color: "#b91c1c", fontSize: 10, fontWeight: 700 }}>URGENT</span>}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                                                    {getRequestCode(d) && <span style={{ marginRight: 4, fontFamily: "monospace" }}>{getRequestCode(d)} ·</span>}
                                                    Dépense le {formatDate(d.dateDepense)}
                                                </div>
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontWeight: 700, fontSize: 14, color: "var(--admin-primary)", whiteSpace: "nowrap" }}>
                                                {formatMontant(d.montantDepense)}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#64748b" }}>
                                                {d.devise || "MAD"}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151" }}>
                                                {d.ordreMissionLibelle || <span style={{ color: "#d1d5db" }}>—</span>}
                                                {(d.ordreMissionLieu || d.ordreMissionVille || d.ordreMissionAdresse) && (
                                                    <div style={{ marginTop: 3, color: "#94a3b8", fontSize: 11 }}>
                                                        {d.ordreMissionLieu || d.ordreMissionVille || d.ordreMissionAdresse}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <StatutBadge statut={d.status} demande={d} />
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", textAlign: "right" }}>
                                                <button
                                                    onClick={() => { setSelectedDemande(d); setShowDetailsModal(true); }}
                                                    style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#475569", padding: "5px 11px", fontSize: 11 }) }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--admin-accent)"; e.currentTarget.style.color = "var(--admin-accent)"; e.currentTarget.style.background = "#f0f4ff"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#fff"; }}
                                                >
                                                    <Eye size={12} /> Détails
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ════════════════ MODAL NOUVELLE NOTE ════════════════ */}
            {showModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Déclarer une note de frais</div>
                                <div className="pcModal__subtitle">Sélectionnez une mission et le type de dépense</div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowModal(false)}><X size={15} /></button>
                        </div>

                        <div className="pcModal__body">
                            <form id="ndfForm" onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

                                {/* Ordre de mission */}
                                <FieldBlock label="Ordre de mission">
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <div className="pcSelectWrap" style={{ flex: 1 }}>
                                            <select className="pcInput" value={form.ordreMissionId} onChange={handleOMChange}>
                                                <option value="">— Aucune mission —</option>
                                                {myApprovedMissions.map(r => (
                                                    <option key={r.id} value={r.ordreMissionId}>
                                                        {r.ordreMissionCode} · {r.ordreMissionLibelle} ({formatDate(r.ordreMissionDateDebut)} → {formatDate(r.ordreMissionDateFin)})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {form.ordreMissionId && (
                                            <button
                                                type="button"
                                                onClick={() => setShowMissionModal(true)}
                                                title="Voir les détails"
                                                style={{ ...btn({ border: "1px solid #bae6fd", background: "#f0f9ff", color: "#0284c7", padding: "7px 10px" }) }}
                                                onMouseEnter={e => e.currentTarget.style.background = "#e0f2fe"}
                                                onMouseLeave={e => e.currentTarget.style.background = "#f0f9ff"}
                                            >
                                                <Info size={15} />
                                            </button>
                                        )}
                                    </div>
                                </FieldBlock>

                                {/* Type de frais */}
                                <FieldBlock label={<>Type de frais <span className="pcReq">*</span></>}>
                                    <div className="pcSelectWrap">
                                        <select
                                            className="pcInput"
                                            value={form.noteDeFraisId}
                                            onChange={e => {
                                                const t = typesDisponibles.find(t => t.id === Number(e.target.value));
                                                setForm(p => ({ ...p, noteDeFraisId: e.target.value, category: t?.category || "" }));
                                            }}
                                            required
                                        >
                                            <option value="">— Sélectionner un type de frais —</option>
                                            {typesDisponibles.map(t => (
                                                <option key={t.id} value={t.id}>{t.category}{t.code ? ` (${t.code})` : ""}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {typesDisponibles.length === 0 && selectedOM && (
                                        <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>Aucun type de frais autorisé pour cette mission.</div>
                                    )}
                                </FieldBlock>

                                {/* Récap type sélectionné */}
                                {selectedType && (
                                    <div style={{ background: "linear-gradient(135deg,#f0fdf4,#f8faff)", border: "1px solid #d1fae5", borderRadius: 10, padding: "12px 14px" }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 10 }}>Règles du type sélectionné</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                                            {[
                                                { k: "Montant min.", v: selectedType.montantMinimum ? `${formatMontant(selectedType.montantMinimum)} ${selectedType.devise || "MAD"}` : "Aucun" },
                                                { k: "Montant max.", v: selectedType.montantMaximum ? `${formatMontant(selectedType.montantMaximum)} ${selectedType.devise || "MAD"}` : "Aucun" },
                                                { k: "Justificatif",  v: selectedType.obligatoire ? `Obligatoire (${selectedType.typeJustificatif})` : "Optionnel", warn: selectedType.obligatoire },
                                            ].map(({ k, v, warn }) => (
                                                <div key={k} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                    <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{k}</div>
                                                    <div style={{ fontSize: 12, fontWeight: 600, color: warn ? "#dc2626" : "#111827" }}>{v}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Date + Montant */}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                                    <FieldBlock label={<>Date de la dépense <span className="pcReq">*</span></>}>
                                        <input className="pcInput" type="date" value={form.dateDepense}
                                               onChange={e => setForm(p => ({ ...p, dateDepense: e.target.value }))}
                                               min={selectedOM?.dateDebut} max={maxDate} required />
                                    </FieldBlock>
                                    <FieldBlock label={<>Montant <span className="pcReq">*</span></>}>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <input className="pcInput" style={{ flex: 1 }} type="number" value={form.montant}
                                                   onChange={e => setForm(p => ({ ...p, montant: e.target.value }))}
                                                   min="0" step="0.01" required />
                                            <div style={{ display: "flex", alignItems: "center", padding: "0 12px", background: "#f8fafc", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#64748b", whiteSpace: "nowrap" }}>
                                                {selectedType?.devise || "MAD"}
                                            </div>
                                        </div>
                                    </FieldBlock>
                                </div>

                                {/* Description */}
                                <FieldBlock label={<>Description <span className="pcReq">*</span></>}>
                                    <textarea className="pcInput pcInput--textarea" value={form.description}
                                              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                              placeholder="Ex : Repas midi avec client X..." required />
                                </FieldBlock>

                                {/* Justificatifs */}
                                <FieldBlock label={<>Justificatifs{selectedType?.obligatoire && <span className="pcReq"> *</span>}</>}>
                                    <label style={{ position: "relative", border: "1.5px dashed #cbd5e1", padding: "20px 16px", borderRadius: 10, textAlign: "center", background: "#f8fafc", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, transition: "border-color .15s" }}
                                           onMouseEnter={e => e.currentTarget.style.borderColor = "var(--admin-accent)"}
                                           onMouseLeave={e => e.currentTarget.style.borderColor = "#cbd5e1"}
                                    >
                                        <Upload size={22} color="#94a3b8" />
                                        <span style={{ fontSize: 12, color: "#64748b" }}>Glissez vos fichiers ici ou <span style={{ color: "var(--admin-accent)", fontWeight: 600 }}>parcourez</span></span>
                                        <input type="file" multiple onChange={e => setForm(p => ({ ...p, justificatifs: [...p.justificatifs, ...Array.from(e.target.files || [])] }))} style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }} />
                                    </label>
                                    {form.justificatifs.length > 0 && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
                                            {form.justificatifs.map((file, i) => (
                                                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f1f5f9", padding: "6px 12px", borderRadius: 7, fontSize: 12 }}>
                                                    <span style={{ color: "#374151" }}>{file.name || file}</span>
                                                    <button type="button" onClick={() => setForm(p => ({ ...p, justificatifs: p.justificatifs.filter((_, j) => j !== i) }))}
                                                            style={{ border: "none", background: "none", cursor: "pointer", padding: 2, display: "flex", alignItems: "center", color: "#ef4444" }}>
                                                        <X size={13} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </FieldBlock>

                            </form>
                        </div>

                        <div className="pcModal__footer">
                            <button type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#374151" }) }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                Annuler
                            </button>
                            <button type="submit" form="ndfForm" disabled={loading}
                                    style={{ ...btn({ border: "none", background: "var(--admin-accent)", color: "#fff", opacity: loading ? .6 : 1, boxShadow: "0 1px 3px rgba(0,0,0,.12)" }) }}
                                    onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = "brightness(1.08)"; }}
                                    onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                            >
                                {loading ? <><Loader size={13} className="spin" /> Envoi...</> : "Soumettre la dépense"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════ MODAL DÉTAILS ════════════════ */}
            {showDetailsModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel" style={{ width: "min(620px,100%)" }}>
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Détails de la note de frais</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                    <CategoryChip cat={getRequestCategory(selectedDemande)} />
                                    {getRequestCode(selectedDemande) && (
                                        <span style={{ fontSize: 11, fontFamily: "monospace", padding: "2px 7px", borderRadius: 5, background: "#f0f4ff", color: "var(--admin-accent)", border: "1px solid #dde6fb", fontWeight: 700 }}>
                                            {getRequestCode(selectedDemande)}
                                        </span>
                                    )}
                                    <StatutBadge statut={selectedDemande.status} demande={selectedDemande} />
                                </div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowDetailsModal(false)}><X size={15} /></button>
                        </div>

                        <div style={{ padding: "18px", overflowY: "auto", maxHeight: "calc(90vh - 130px)", display: "flex", flexDirection: "column", gap: 16 }}>

                            {/* Récap montant */}
                            <div style={{ background: "linear-gradient(135deg,#f0fdf4,#f8faff)", border: "1px solid #d1fae5", borderRadius: 12, padding: "14px 16px" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 12 }}>Informations financières</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>Montant</div>
                                        <div style={{ fontSize: 18, fontWeight: 700, color: "#0369a1" }}>{formatMontant(selectedDemande.montantDepense)} <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>{selectedDemande.devise || "MAD"}</span></div>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>Date dépense</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{formatDate(selectedDemande.dateDepense)}</div>
                                    </div>
                                    {/*<div style={{ display: "flex", flexDirection: "column", gap: 3 }}>*/}
                                    {/*    <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>Centre de coûts</div>*/}
                                    {/*    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{selectedDemande.centreCouts || "—"}</div>*/}
                                    {/*</div>*/}
                                </div>
                            </div>

                            {/* Mission liée */}
                            {selectedDemande.ordreMissionId && (
                                <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px" }}>
                                    <Briefcase size={16} color="#64748b" style={{ flexShrink: 0 }} />
                                    <div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>Mission liée</div>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2 }}>
                                            {selectedDemande.ordreMissionCode || "—"} · {selectedDemande.ordreMissionLibelle || "—"}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <FieldBlock label="Description">
                                <ReadBox>{selectedDemande.description || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Non renseignée</span>}</ReadBox>
                            </FieldBlock>

                            {selectedDemande.justificatifs?.length > 0 && (
                                <FieldBlock label="Justificatifs">
                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        {selectedDemande.justificatifs.map((f, i) => (
                                            <div key={i} style={{ fontSize: 12, padding: "7px 12px", background: "#f1f5f9", borderRadius: 7, color: "#374151" }}>{f}</div>
                                        ))}
                                    </div>
                                </FieldBlock>
                            )}

                            {(selectedDemande.managerComment || selectedDemande.adminComment) && (
                                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: "#b91c1c", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".05em" }}>
                                        <AlertCircle size={13} /> Commentaire de rejet
                                    </div>
                                    <div style={{ fontSize: 13, color: "#991b1b", lineHeight: 1.5 }}>{selectedDemande.managerComment || selectedDemande.adminComment}</div>
                                </div>
                            )}
                        </div>

                        <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb", background: "#fafbfc", display: "flex", justifyContent: "flex-end" }}>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#374151" }) }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════════════════ MODAL DÉTAILS MISSION ════════════════ */}
            {showMissionModal && selectedOM && (
                <div className="pcModal">
                    <div className="pcModal__panel" style={{ width: "min(560px,100%)" }}>
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Détails de la mission</div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                                    <span style={{ fontSize: 11, fontFamily: "monospace", padding: "2px 7px", borderRadius: 5, background: "#f0f4ff", color: "var(--admin-accent)", border: "1px solid #dde6fb", fontWeight: 700 }}>{getMissionField(selectedOM, "code", "ordreMissionCode")}</span>
                                    <span style={{ fontSize: 12, color: "#64748b" }}>{getMissionField(selectedOM, "libelle", "ordreMissionLibelle")}</span>
                                </div>
                            </div>
                            <button className="pcIconBtn pcIconBtn--close" onClick={() => setShowMissionModal(false)}><X size={15} /></button>
                        </div>

                        <div style={{ padding: "18px", overflowY: "auto", maxHeight: "calc(90vh - 130px)", display: "flex", flexDirection: "column", gap: 16 }}>
                            <div style={{ background: "linear-gradient(135deg,#f0fdf4,#f8faff)", border: "1px solid #d1fae5", borderRadius: 12, padding: "14px 16px" }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 12 }}>Informations</div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 }}>
                                    {[
                                        { k: "Date début",      v: formatDate(getMissionDateDebut(selectedOM)) },
                                        { k: "Date fin",        v: formatDate(getMissionDateFin(selectedOM)) },
                                        { k: "Lieu",            v: getMissionLieu(selectedOM) || "—" },
                                        // { k: "Centre de coût",  v: selectedOM.centreCout || "—" },
                                    ].map(({ k, v }) => (
                                        <div key={k} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{k}</div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{v}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <FieldBlock label="Objet de la mission">
                                <ReadBox>{getMissionField(selectedOM, "objetMission", "ordreMissionObjet") || getMissionField(selectedOM, "description", "ordreMissionDescription") || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Non renseigné</span>}</ReadBox>
                            </FieldBlock>
                        </div>

                        <div style={{ padding: "12px 18px", borderTop: "1px solid #e5e7eb", background: "#fafbfc", display: "flex", justifyContent: "flex-end" }}>
                            <button
                                onClick={() => setShowMissionModal(false)}
                                style={{ ...btn({ border: "1px solid #e2e8f0", background: "#fff", color: "#374151" }) }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExpenseManagement;
